import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Form, Select, Menu, Dropdown, TreeSelect, Typography } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { useHistory, NavLink } from 'react-router-dom'
import qs from 'qs'

import {
  currentTime,
  getBizArrFromUserBiz,
  selectBizFromBizArr,
  transformEntityTree,
  findTreeNode,
  lsGetItem,
  lsSetItem,
  isMobile,
  isInValidData,
} from '../utils/util'
import {
  businessLineMap,
  businessLineTextMap,
  userTypeMap,
  districtLevelMap,
} from '../utils/consts'
import {
  useUserResources,
  useAppSelector,
  useAppDispatch,
  useDataCenterSearchParams,
} from '../utils/hooks'
import { APP, DATACENTER } from '../redux/actionTypes'

const { Option } = Select
const { Text } = Typography

const getFirstEntity = (entityList: any[]) => {
  entityList = entityList || []
  const walk = (entityList: any[]): any => {
    for (let i = 0; i < entityList.length; i++) {
      const item = entityList[i]
      if (item.entityCode) {
        return item
      } else if (item.children) {
        return walk(item.children)
      }
    }
  }
  return walk(entityList)
}

const getPathKey = (path: string) =>
  path.includes('/dataCenter') ? 0 : path.includes('/manageCenter') ? 1 : 2

const Header: React.FC = () => {
  const history = useHistory()
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const timeRef = useRef<HTMLDivElement>(null)
  const [treeData, setTreeData] = useState<Entities>([])
  const [entityName, setEntityName] = useState<string>()
  const [app] = useAppSelector(state => [state.app])
  const { region, userInfo: { user, entity } = {}, weather: { detail, temperature } = {} } = app
  const [dataCenterResource, backgroundManage, manageCenterResource] = useUserResources(
    resources => [resources.dataCenter, resources.backgroundManage, resources.manageCenter]
  )
  const path = location.hash.replace('#', '')
  const [activeKey, setActiveKey] = useState(getPathKey(path))

  const currentPath = useMemo(() => {
    return {
      isDataCenter: path.includes('/dataCenter'),
      isManageCenter: path.includes('/manageCenter'),
    }
  }, [path])

  const searchStr = useMemo(() => {
    return encodeURIComponent(
      btoa(
        qs.stringify({
          entityCode: region.entityCode,
          adCode: region.adCode,
          businessType: app.selectedBiz,
        })
      )
    )
  }, [region, app.selectedBiz])

  const searchParams = useDataCenterSearchParams()

  useEffect(() => {
    if (lsGetItem('__entityCode__')) {
      searchParams.entityCode = searchParams.entityCode || lsGetItem('__entityCode__')!
    } else if (lsGetItem('__adCode__')) {
      searchParams.adCode = searchParams.adCode || lsGetItem('__adCode__')!
    }
  }, [searchParams])

  useEffect(() => {
    setActiveKey(getPathKey(path))
  }, [path])

  // 登陆后，选择默认的实体，更新app.region
  useEffect(() => {
    if (entity) {
      transformEntityTree(entity, (item: any) => {
        if (user?.userType === userTypeMap.entityUser) {
          const disableArea = [districtLevelMap.country, districtLevelMap.province]
          item.disabled = disableArea.includes(+item.level)
        }
      })
      setTreeData(entity)
      // url 上有搜索参数，优先使用该参数
      // 否则走原有逻辑
      let targetNode
      if (searchParams.entityCode) {
        targetNode = findTreeNode(entity, 'entityCode', +searchParams.entityCode)
      } else if (searchParams.adCode) {
        targetNode = findTreeNode(entity, 'adCode', searchParams.adCode)
      }

      if (targetNode) {
        updateRegion(targetNode)
        form.setFieldsValue({ region: targetNode.value })
        return
      }

      if (user?.userType === userTypeMap.entityUser) {
        // 实体用户默认取第一个实体
        const firstEntity = getFirstEntity(entity)
        updateRegion(firstEntity)
        form.setFieldsValue({ region: entity[0]?.value })
      } else {
        // transformEntity(entity)
        // setTreeData(entity)
        updateRegion(entity[0])
        form.setFieldsValue({ region: entity[0]?.value })
      }
    }
  }, [entity])

  // 用户业务线权限发生变化，重置业务线和默认选择的业务线
  useEffect(() => {
    const searchBizType = +(searchParams.businessType as string)
    const isValidSearchBiz =
      user?.businessType === businessLineMap.double
        ? searchBizType <= user?.businessType
        : searchBizType === user?.businessType
    if (isValidSearchBiz) {
      form.setFieldsValue({ bikeType: searchBizType })
      handleBizChange(searchBizType, false)
      return
    }

    if (user?.userType === userTypeMap.entityUser) {
      const bizArr = getBizArrFromUserBiz(user?.businessType)
      const biz = selectBizFromBizArr(bizArr)
      handleBizChange(biz, false)
      form.setFieldsValue({ bikeType: biz })
    } else if (user?.userType === userTypeMap.platformUser) {
      const bizArr = getBizArrFromUserBiz(user?.businessType)
      const biz = selectBizFromBizArr(bizArr)
      handleBizChange(biz, false)
      form.setFieldsValue({ bikeType: biz })
    }
  }, [user])

  useEffect(() => {
    if (region) {
      form.setFieldsValue({ region: region.adCode || region.entityCode })
    }
  }, [region])

  useEffect(() => {
    if (region.adCode || region.entityCode) {
      dispatch({
        type: APP.GET_WEATHER,
        payload: {
          adCode: region.adCode,
          entityCode: region.entityCode,
        },
      })
    }
  }, [region])

  useEffect(() => {
    if (timeRef) {
      const timerId = setInterval(() => {
        timeRef.current && (timeRef.current.innerHTML = currentTime())
      }, 1000)
      return () => clearInterval(timerId)
    }
  }, [timeRef])

  useEffect(() => {
    if ((region.adCode || region.entityCode) && app.selectedBiz && currentPath.isDataCenter) {
      history.push(`/dataCenter?${searchStr}`)
    }
  }, [region, app.selectedBiz])

  const handleDataCenterClick = () => {
    setActiveKey(0)
    history.push(`/dataCenter?${searchStr}`)
  }

  const handleAuthClick = () => {
    setActiveKey(1)
    history.push('/manageCenter')
  }

  const handleLogoutClick = () => {
    dispatch({
      type: APP.LOG_OUT,
      cb: () => {
        history.push('/login')
      },
    })
  }

  const setLsItem = (key: '__entityCode__' | '__adCode__', val: string) => {
    if (key === '__entityCode__') {
      lsSetItem('__adCode__', '')
    } else if (key === '__adCode__') {
      lsSetItem('__entityCode__', '')
    }
    lsSetItem(key, val + '')
  }

  const updateRegion = (nodeProps = {} as Entity) => {
    if (nodeProps.entityCode) {
      const { lng, lat, entityCode, entityBusinessType, entityName, level, titleFontSize } =
        nodeProps
      setLsItem('__entityCode__', nodeProps.entityCode + '')
      setEntityName(entityName as string)
      // 更新 region 和 businessType， 进入到腾讯地图
      dispatch({
        type: APP.UPDATE_STATE,
        payload: {
          region: { entityCode, lng, lat, level, titleFontSize },
          userInfo: {
            ...app.userInfo,
            user: {
              ...user,
              defaultUserBusinessType: user?.defaultUserBusinessType || user?.businessType,
              businessType: entityBusinessType,
              businessTypeFrom: 'entityBusinessType' + entityBusinessType,
            },
          },
        },
      })
      const bizArr = getBizArrFromUserBiz(entityBusinessType)
      const biz = selectBizFromBizArr(bizArr)
      // 更新用户资源点
      dispatch({
        type: APP.GET_USER_RESOURCES,
        payload: { entityCode: entityCode, businessType: biz },
      })
    } else if (nodeProps.adCode) {
      setLsItem('__adCode__', nodeProps.adCode)
      // 拿到区域code
      setEntityName('')
      dispatch({
        type: APP.UPDATE_STATE,
        payload: {
          region: { adCode: nodeProps.adCode, level: nodeProps.level },
          userInfo: {
            ...app.userInfo,
            user: {
              ...user,
              defaultUserBusinessType: user?.defaultUserBusinessType || user?.businessType,
              businessType: user?.defaultUserBusinessType || user?.businessType,
              businessTypeFrom:
                'userBusinessType' + (user?.defaultUserBusinessType || user?.businessType),
            },
          },
        },
      })
      const bizArr = getBizArrFromUserBiz(user?.businessType)
      const biz = selectBizFromBizArr(bizArr)
      dispatch({
        type: APP.GET_USER_RESOURCES,
        payload: { adCode: nodeProps.adCode, businessType: biz },
      })
      dispatch({
        type: DATACENTER.UPDATE_STATE,
        payload: {
          fullScreen: undefined,
        },
      })
    }
  }

  const handleRegionSelect = (value: any, node: any) => {
    if (value === region?.entityCode || value === region?.adCode) {
      return
    }
    updateRegion(node)
  }

  const handleBizChange = (value: number, getResource = true) => {
    // getResource 只在手动切换业务线时为 true，此时重新获取业务线
    dispatch({
      type: APP.UPDATE_STATE,
      payload: {
        selectedBiz: value,
      },
    })
    // 更新用户资源点
    if ((region.adCode !== undefined || region.entityCode !== undefined) && getResource) {
      dispatch({
        type: APP.GET_USER_RESOURCES,
        payload: { adCode: region.adCode, entityCode: region.entityCode, businessType: value },
      })
    }
  }

  const isSingleEntity = (tree: any) => {
    return tree?.length === 1 && isInValidData(tree[0]?.children)
  }

  const menu = (
    <Menu>
      {backgroundManage.index ? (
        <Menu.Item key={1}>
          <NavLink to="/background" activeClassName="menu-selected">
            后台管理
          </NavLink>
        </Menu.Item>
      ) : null}
      <Menu.Item key={2} onClick={handleLogoutClick}>
        退出登录
      </Menu.Item>
    </Menu>
  )

  return (
    <div className="header betweenCenter relative">
      <div className="leftBar ylCenter"></div>
      <div className="rightBar yrCenter"></div>
      <Form layout="inline" form={form} className="grayFont07">
        {path.includes('/dataCenter') ? (
          <>
            <Form.Item label="" name="region">
              <TreeSelect
                showSearch
                treeNodeFilterProp="label"
                style={{ width: 150 }}
                className="customTreeSelect"
                dropdownClassName="customTreeSelectDropdown"
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                // @ts-ignore
                treeData={treeData}
                placeholder="请选择"
                treeDefaultExpandAll
                onSelect={handleRegionSelect}
              />
            </Form.Item>
            <Form.Item label="" name="bikeType">
              <Select
                className="custom-select"
                dropdownClassName="dropdown-style"
                placeholder="车辆类型"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  // @ts-ignore
                  option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                onChange={handleBizChange as any}
              >
                {getBizArrFromUserBiz(user?.businessType).map(biz => (
                  <Option value={biz} key={biz}>
                    {businessLineTextMap[biz as keyof typeof businessLineTextMap]}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </>
        ) : null}
        {isMobile() && path.includes('/dataCenter') ? null : (
          <div className="centerCenter" id="tempTimeContainer">
            <div className="pfRegular mr10">
              {(detail || '') + ' ' + (temperature ? temperature + '°C' : '')}{' '}
            </div>
            <div className="mr10" ref={timeRef}>
              {currentTime()}
            </div>
          </div>
        )}
      </Form>
      <Text
        className="header-title absCenter"
        style={{ fontSize: region.titleFontSize, lineHeight: '80px', textAlign: 'center' }}
        ellipsis={{ tooltip: { title: entityName, placement: 'right' } }}
      >
        {entityName || '骑行智能监管平台'}
      </Text>
      <div className="centerCenter zi10 grayFont07">
        {!dataCenterResource.index && isSingleEntity(treeData) ? (
          ''
        ) : (
          <div
            className={`mr10 pointer ${activeKey === 0 ? 'hightLight' : ''}`}
            onClick={handleDataCenterClick}
          >
            数据中心
          </div>
        )}
        {manageCenterResource.index ? (
          <>
            {!dataCenterResource.index && isSingleEntity(treeData) ? (
              ''
            ) : (
              <div className="header-divider mr10"></div>
            )}
            <div
              className={`mr10 pointer ${activeKey === 1 ? 'hightLight' : ''}`}
              onClick={handleAuthClick}
            >
              管理中心
            </div>
          </>
        ) : null}
        <div className="header-divider mr10"></div>
        {/* @ts-ignore */}
        <Dropdown overlay={menu}>
          <a className="ant-dropdown-link" onClick={e => e.preventDefault()}>
            {user?.userName} <DownOutlined />
          </a>
        </Dropdown>
      </div>
    </div>
  )
}

export default Header
